import { CurrencyType } from "@/lib/gql/__generated__/graphql";

import type { ScConnectedToggleViewModel } from "./create-sc-connected-on-view-model-updates";

/**
 * SC 連携 OFF 時に initInput から復元する最小形。
 *
 * スキーマ上必須のキーは必須。Zod `.nullish()` 由来で InputModel 上もキーが
 * optional になるものだけ `?` を残す（nullish は value 側の Nullable に閉じる）。
 */
export type ScConnectedRestoreInput = {
	isRoomPriceFree: boolean;
	price: {
		amount: number;
		currencyType: CurrencyType;
	};
};

/**
 * onSetViewModel(<K>(values: { key: K; value: V[K] }[])) が異種キー配列を受け取るための戻り値型。
 * K が keyof V に推論されると呼び出し側のキャストが不要になる。
 */
type ScConnectedViewModelUpdates<TViewModel extends ScConnectedToggleViewModel> = {
	key: keyof TViewModel;
	value: TViewModel[keyof TViewModel];
}[];

/**
 * SC 連携トグル ON→OFF 時の ViewModel 更新を作成する。
 *
 * ページ読み込み時の initInput（サーバー値）から料金を復元する。
 * staticPriceParams は復元しない（再設定で対応）。
 *
 * 戻り値の型アサーションは、リテラル配列をジェネリック ViewModel の更新型へ
 * 対応づける境界。呼び出し側では不要。
 */
export const createScConnectedOffViewModelUpdates = <
	TViewModel extends ScConnectedToggleViewModel,
	TInitInput extends ScConnectedRestoreInput,
>(
	viewModel: TViewModel,
	initInput: TInitInput,
): ScConnectedViewModelUpdates<TViewModel> => {
	const amount = initInput.price.amount ?? viewModel.price.amount.value ?? 0;

	const updates = [
		{
			key: "isSCConnected" as const,
			value: {
				...viewModel.isSCConnected,
				isEdited: true as const,
				value: false,
			},
		},
		{
			key: "isRoomPriceFree" as const,
			value: {
				...viewModel.isRoomPriceFree,
				isEdited: true as const,
				value: initInput.isRoomPriceFree,
			},
		},
		{
			key: "price" as const,
			value: {
				...viewModel.price,
				amount: {
					...viewModel.price.amount,
					isEdited: true as const,
					value: amount,
				},
				currencyType: {
					...viewModel.price.currencyType,
					isEdited: true as const,
					value: initInput.price.currencyType ?? viewModel.price.currencyType.value ?? CurrencyType.Jpy,
				},
			},
		},
	];

	return updates as ScConnectedViewModelUpdates<TViewModel>;
};
